import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { QrBackground, QrForeground, QrLogoStyle } from '../api/types';
import { Dropdown, type DropdownOption } from './Dropdown';

export interface ImageStyle {
  foreground: QrForeground;
  background: QrBackground;
  logoStyle: QrLogoStyle;
}

export const DEFAULT_IMAGE_STYLE: ImageStyle = {
  foreground: 'Green',
  background: 'White',
  logoStyle: 'Default',
};

const INKS: QrForeground[] = ['Green', 'Black', 'White'];

/** What each ink actually looks like, so the list shows the colour rather than naming it. */
const SWATCH: Record<QrForeground, string> = {
  Green: '#00783f',
  Black: '#000000',
  White: '#ffffff',
};

/**
 * The grounds each ink can be read against - the same rule the API enforces, mirrored here so the form cannot
 * offer a pair it would refuse with a 400. Transparent goes with any ink, because what the code is dropped
 * onto is the layout's business and not something this application can see.
 */
const GROUNDS: Record<QrForeground, QrBackground[]> = {
  Green: ['White', 'Transparent'],
  Black: ['White', 'Transparent'],
  White: ['Black', 'Transparent'],
};

/**
 * The three controls in the strip under the code: which ink, on what ground, and how the butterfly at its
 * centre is drawn. Each list is deliberately short - an arbitrary colour is how a print run comes back
 * unscannable.
 */
export function ImageStyleFields({
  idPrefix,
  value,
  onChange,
  disabled = false,
}: {
  idPrefix: string;
  value: ImageStyle;
  onChange: (style: ImageStyle) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const labels = useId();

  const inkOptions: DropdownOption<QrForeground>[] = INKS.map((ink) => ({
    value: ink,
    label: t(`qrCode.ink${ink}`),
    swatch: (
      <span
        className={`picker__swatch${ink === 'White' ? ' is-light' : ''}`}
        style={{ background: SWATCH[ink] }}
        aria-hidden="true"
      />
    ),
  }));

  const groundOptions: DropdownOption<QrBackground>[] = GROUNDS[value.foreground].map((ground) => ({
    value: ground,
    label: t(`qrCode.background${ground}`),
    // Transparent has no colour to show, so it shows the chequerboard that stands for one.
    swatch: (
      <span
        className={`picker__swatch is-square${ground === 'Transparent' ? ' is-transparent' : ''}${
          ground === 'White' ? ' is-light' : ''
        }`}
        style={ground === 'Transparent' ? undefined : { background: ground === 'Black' ? '#000' : '#fff' }}
        aria-hidden="true"
      />
    ),
  }));

  const logoOptions: DropdownOption<QrLogoStyle>[] = [
    { value: 'Default', label: t('qrCode.logoStyleDefault') },
    { value: 'Square', label: t('qrCode.logoStyleSquare') },
    { value: 'None', label: t('qrCode.logoStyleNone') },
  ];

  return (
    <div className="qr-widget__settings">
      <div className="qr-widget__setting">
        <span className="qr-widget__micro" id={`${labels}-ink`}>
          {t('qrCode.ink')}
        </span>
        <Dropdown
          id={`${idPrefix}-ink`}
          labelledBy={`${labels}-ink`}
          value={value.foreground}
          options={inkOptions}
          disabled={disabled}
          onChange={(foreground) => {
            const grounds = GROUNDS[foreground];

            onChange({
              ...value,
              foreground,
              // The ground moves with the ink when it has to: white on white is not a state this form should be
              // able to reach.
              background: grounds.includes(value.background) ? value.background : grounds[0],
            });
          }}
        />
      </div>

      <div className="qr-widget__setting">
        <span className="qr-widget__micro" id={`${labels}-background`}>
          {t('qrCode.background')}
        </span>
        <Dropdown
          id={`${idPrefix}-background`}
          labelledBy={`${labels}-background`}
          value={value.background}
          options={groundOptions}
          disabled={disabled}
          title={t('qrCode.backgroundTransparentHint')}
          onChange={(background) => onChange({ ...value, background })}
        />
      </div>

      <div className="qr-widget__setting">
        <span className="qr-widget__micro" id={`${labels}-logo`}>
          {t('qrCode.logoStyle')}
        </span>
        <Dropdown
          id={`${idPrefix}-logo-style`}
          labelledBy={`${labels}-logo`}
          value={value.logoStyle}
          options={logoOptions}
          disabled={disabled}
          onChange={(logoStyle) => onChange({ ...value, logoStyle })}
        />
      </div>
    </div>
  );
}
